'use client';
import { faStar as faRegularStar } from '@fortawesome/free-regular-svg-icons';
import {
    faArrowUpRightFromSquare,
    faEllipsis,
    faLock,
    faPlus,
    faStar as faSolidStar
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AnimatePresence, motion } from 'framer-motion';
import React, { forwardRef, useCallback, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';

import usePersistentState from '@hooks/usePersistentState';
import { Badge, BetaBadge, InfoTooltip } from '@ui';
import { Accordion } from '@ui/Accordion/Accordion';
import Tooltip from '@ui/Tooltip';
import { cx } from 'class-variance-authority';
import type { Tab } from 'components/Layout/ModulePageLayout';
import type { Page } from 'components/Layout/ModulePageLayout/clubPages';
import Link from 'next/link';

const SIDEBAR_ACCORDION_KEY = 'sidebar-accordion-open-pages';
const SIDEBAR_SETTINGS_KEY = 'sidebar-accordion-open-settings';
const DEFAULT_OPEN_PAGES: string[] = [];

type PageWithTabs = Page & {
    tabs?: Tab[];
};

type QuickAction = NonNullable<Page['quickActions']>[number];

interface ModuleAccordionProps {
    pages: PageWithTabs[];
    /** Current path without query string — used to resolve active state for explicit-link tabs. */
    currentPath?: string;
    isActiveTab: (pageLink?: string, key?: string, activeKeys?: string[]) => boolean;
    pageIsActive: (link: string) => boolean;
    favorites?: Tab[];
    addFavorite: (favoriteId: Omit<Tab, 'id'> & { id?: string }) => void;
    removeFavorite: (favoriteId: Tab['id']) => void;
    subscribedPlans?: string[];
}

export type AccordionHandle = {
    expandAll: () => void;
    collapseAll: () => void;
};

export const SideBarAccordion = forwardRef<AccordionHandle, ModuleAccordionProps>(
    (
        {
            pages,
            currentPath = '',
            isActiveTab,
            pageIsActive,
            favorites = [],
            addFavorite,
            removeFavorite,
            subscribedPlans = []
        },
        ref
    ) => {
        const { t } = useTranslation('common');
        const [openPagesRaw, setOpenPages] = usePersistentState<string[]>(SIDEBAR_ACCORDION_KEY, DEFAULT_OPEN_PAGES);
        const [openSettingsRaw, setOpenSettings] = usePersistentState<string[]>(
            SIDEBAR_SETTINGS_KEY,
            DEFAULT_OPEN_PAGES
        );
        const openPages = openPagesRaw ?? [];
        const openSettings = openSettingsRaw ?? [];

        const getTabFavoriteId = useCallback(
            (pageLink: string, tabKey: string) => `${pageLink || 'root'}-${tabKey || 'default'}`,
            []
        );

        const togglePage = useCallback((link: string) => {
            setOpenPages((prev = []) => (prev.includes(link) ? prev.filter((l) => l !== link) : [...prev, link]));
        }, []);

        const toggleSettings = useCallback((link: string) => {
            setOpenSettings((prev = []) => (prev.includes(link) ? prev.filter((l) => l !== link) : [...prev, link]));
        }, []);

        const handleExpandAll = useCallback(() => {
            setOpenPages(pages.map((p) => p.link));
        }, [pages]);

        const handleCollapseAll = useCallback(() => {
            setOpenPages([]);
            setOpenSettings([]);
        }, []);

        useImperativeHandle(
            ref,
            () => ({
                expandAll: handleExpandAll,
                collapseAll: handleCollapseAll
            }),
            [handleExpandAll, handleCollapseAll]
        );

        return (
            <div className="tw-flex tw-flex-col tw-justify-center">
                {pages.map((page, index) => {
                    const currentPageLink = page?.link?.split('/')[3];
                    const isPageActive = pageIsActive(currentPageLink);
                    const pageTabs = page.tabs ?? [];
                    // Explicit-link tabs (e.g. the Einstellungen hub) match by URL, not page segment.
                    const isAnyLinkTabActive = pageTabs.some((tab) => tab.link && currentPath.startsWith(tab.link));
                    const isOpen = isPageActive || isAnyLinkTabActive || openPages.includes(page.link);
                    const isLastPage = index === pages.length - 1;
                    const isLocked = page.requiredPlan !== undefined && !subscribedPlans.includes(page.requiredPlan);

                    const useGrouping = pageTabs.length >= 5;
                    const operativeTabs = useGrouping ? pageTabs.filter((tab) => tab.group !== 'settings') : pageTabs;
                    const settingsTabs = useGrouping ? pageTabs.filter((tab) => tab.group === 'settings') : [];
                    const hasSettingsTabs = settingsTabs.length > 0;
                    const isSettingsOpen = openSettings.includes(page.link);
                    const quickActions = page.quickActions ?? (page.quickAction ? [page.quickAction] : []);
                    // Auto-expand settings section when user is on a settings tab
                    const isAnySettingsTabActive = settingsTabs.some((tab) =>
                        isActiveTab(currentPageLink, tab.key, tab.activeKeys)
                    );
                    const showSettings = isSettingsOpen || isAnySettingsTabActive;

                    return (
                        <div key={page.link} className="tw-flex tw-flex-col tw-items-stretch">
                            {/* MODULE HEADER */}
                            <div className="tw-group/header tw-mb-0 tw-ml-1 tw-flex tw-items-center">
                                <button
                                    type="button"
                                    onClick={() => togglePage(page.link)}
                                    className={cx(
                                        'tw-group tw-relative tw-flex tw-min-w-0 tw-flex-1 tw-items-center tw-gap-2 tw-rounded-md tw-border-0 tw-bg-transparent tw-px-3 tw-py-1 tw-text-left tw-text-sm tw-font-semibold tw-text-accents-8 tw-transition-colors hover:tw-text-success',
                                        isPageActive &&
                                            'before:tw-absolute before:tw-bottom-0.5 before:tw-left-1 before:tw-top-0.5 before:tw-inline-block before:tw-w-[3px] before:tw-rounded before:tw-bg-primary before:tw-content-[""]'
                                    )}
                                >
                                    {page.icon && (
                                        <FontAwesomeIcon
                                            className={cx(
                                                'tw-ml-1 tw-min-w-4 tw-transition-colors',
                                                isLocked ? 'tw-text-warning group-hover:tw-text-success' : ''
                                            )}
                                            icon={page.icon}
                                        />
                                    )}
                                    <span
                                        className={cx(
                                            'tw-truncate tw-transition-colors',
                                            isLocked && 'tw-text-warning group-hover:tw-text-success'
                                        )}
                                    >
                                        {page.title}
                                    </span>
                                    {/* Push the Beta badge to the right edge, away from the title. */}
                                    {page.beta && (
                                        <span className="tw-ml-auto tw-inline-flex">
                                            <BetaBadge />
                                        </span>
                                    )}
                                    {page.infoBadge && (
                                        <Tooltip
                                            autoPlacement
                                            tooltip={
                                                <p className="tw-my-2 tw-max-w-[220px]">{page.infoBadge.tooltip}</p>
                                            }
                                            className="tw-ml-auto tw-inline-flex"
                                            withArrow
                                        >
                                            <Badge size="xs" color="light">
                                                <FontAwesomeIcon icon={page.infoBadge.icon} />
                                            </Badge>
                                        </Tooltip>
                                    )}
                                    {isLocked && (
                                        <Tooltip
                                            tooltip="Kein aktives Abonnement für dieses Modul"
                                            placement="right"
                                            className="tw-ml-auto"
                                        >
                                            <FontAwesomeIcon
                                                icon={faLock}
                                                className="tw-text-xs tw-text-warning tw-transition-colors group-hover:tw-text-success"
                                            />
                                        </Tooltip>
                                    )}
                                </button>
                                {!isLocked && quickActions.length > 0 && (
                                    <div className="tw-mr-1.5 tw-flex tw-shrink-0 tw-items-center tw-gap-1 tw-opacity-70 tw-transition-opacity tw-duration-150 group-hover/header:tw-opacity-100">
                                        {quickActions.map((action, actionIndex) => (
                                            <QuickActionButton
                                                key={`${page.link}-${action.label}`}
                                                action={action}
                                                priority={Boolean(
                                                    action.prominent ?? (actionIndex === 0 && quickActions.length === 1)
                                                )}
                                            />
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* OPERATIVE TABS + SETTINGS TOGGLE */}
                            <AnimatePresence initial={false}>
                                {isOpen && (operativeTabs.length > 0 || hasSettingsTabs) && (
                                    <motion.div
                                        key={`${page.link}-content`}
                                        initial={{ height: 0, opacity: 0 }}
                                        animate={{ height: 'auto', opacity: 1 }}
                                        exit={{ height: 0, opacity: 0 }}
                                        transition={{ duration: 0.2 }}
                                        className="tw-overflow-hidden"
                                    >
                                        {/* Operative tabs */}
                                        {operativeTabs.length > 0 && (
                                            <ul className="tw-mb-0 tw-mt-1 tw-list-none tw-pl-0">
                                                {operativeTabs.map((tab) =>
                                                    renderTab(
                                                        tab,
                                                        page,
                                                        currentPageLink,
                                                        currentPath,
                                                        favorites,
                                                        getTabFavoriteId,
                                                        isActiveTab,
                                                        addFavorite,
                                                        removeFavorite
                                                    )
                                                )}
                                            </ul>
                                        )}

                                        {/* Einzelner Settings-Tab: direkt anzeigen, kein Accordion nötig */}
                                        {hasSettingsTabs && settingsTabs.length === 1 && (
                                            <ul className="tw-mb-0 tw-mt-0 tw-list-none tw-pl-0">
                                                {settingsTabs.map((tab) =>
                                                    renderTab(
                                                        tab,
                                                        page,
                                                        currentPageLink,
                                                        currentPath,
                                                        favorites,
                                                        getTabFavoriteId,
                                                        isActiveTab,
                                                        addFavorite,
                                                        removeFavorite
                                                    )
                                                )}
                                            </ul>
                                        )}

                                        {hasSettingsTabs && settingsTabs.length > 1 && (
                                            <Accordion
                                                isOpen={showSettings}
                                                onToggle={() => toggleSettings(page.link)}
                                                className="!tw-my-0 tw-ml-1 tw-rounded-md tw-border-0 tw-bg-transparent tw-px-2"
                                                triggerClassName={cx(
                                                    'tw-min-h-0 tw-items-center tw-gap-2 tw-rounded-md tw-pr-1  tw-py-1 tw-pl-6 tw-text-left hover:tw-bg-accents-1 hover:tw-text-accents-9',
                                                    showSettings && 'tw-bg-accents-1/70'
                                                )}
                                                titleClassName={cx(
                                                    'tw-flex tw-w-full tw-items-center tw-gap-2 tw-text-sm tw-font-semibold tw-leading-none !tw-text-accents-8',
                                                    showSettings && 'tw-text-accents-9'
                                                )}
                                                iconClassName={cx(
                                                    'tw-shrink-0 tw-text-accents-6 [&>svg]:tw-h-3.5 [&>svg]:tw-w-3.5',
                                                    showSettings && 'tw-text-accents-7'
                                                )}
                                                contentClassName="!tw-mt-0 !tw-pr-0 !tw-pl-1 !tw-pb-0 tw-text-inherit"
                                                title={
                                                    <div className="tw-flex tw-w-full tw-items-center tw-gap-2 tw-text-left">
                                                        <FontAwesomeIcon
                                                            icon={faEllipsis}
                                                            className="tw-ml-1 tw-min-w-4 tw-text-xs"
                                                        />
                                                        <span className="tw-truncate">{t('sidebar.advanced')}</span>
                                                        {/* <span className="tw-ml-auto tw-text-[10px] tw-font-medium tw-text-accents-6">
                                                            {settingsTabs.length}
                                                        </span> */}
                                                    </div>
                                                }
                                            >
                                                <ul className="tw-mb-0 tw-list-none tw-pl-0">
                                                    {settingsTabs.map((tab) =>
                                                        renderTab(
                                                            tab,
                                                            page,
                                                            currentPageLink,
                                                            currentPath,
                                                            favorites,
                                                            getTabFavoriteId,
                                                            isActiveTab,
                                                            addFavorite,
                                                            removeFavorite
                                                        )
                                                    )}
                                                </ul>
                                            </Accordion>
                                        )}
                                    </motion.div>
                                )}
                            </AnimatePresence>

                            {!isLastPage && <div className="ml-1 tw-my-1 tw-h-[1px] tw-w-full tw-bg-accents-1" />}
                        </div>
                    );
                })}
            </div>
        );
    }
);

// Renders one sidebar tab as an internal <Link>, or as an external <a> (new tab) when tab.external is set.
function renderTab(
    tab: Tab,
    page: PageWithTabs,
    currentPageLink: string,
    currentPath: string,
    favorites: Tab[],
    getTabFavoriteId: (pageLink: string, tabKey: string) => string,
    isActiveTab: (pageLink?: string, key?: string, activeKeys?: string[]) => boolean,
    addFavorite: (fav: Omit<Tab, 'id'> & { id?: string }) => void,
    removeFavorite: (id: Tab['id']) => void
) {
    const isExternal = Boolean(tab.external);
    const href = tab.link ?? `${page.link}/${tab.key}`;
    // External tabs leave the module and are never highlighted as the active route.
    const isActive = isExternal
        ? false
        : tab.link
          ? currentPath.startsWith(tab.link)
          : isActiveTab(currentPageLink, tab.key, tab.activeKeys);
    const { id: _tabId, ...tabWithoutId } = tab;
    const tabFavoriteId = _tabId ?? getTabFavoriteId(page.link, tab.key);
    const favoriteMatch = favorites.find((f) => f.id === tabFavoriteId);
    const isFavorite = Boolean(favoriteMatch);

    const handleFavoriteToggle = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        event.stopPropagation();
        if (isFavorite && favoriteMatch?.id) {
            removeFavorite(favoriteMatch.id);
        } else {
            addFavorite({
                ...tabWithoutId,
                id: tabFavoriteId,
                pageLink: page.link,
                pageTitle: page.title
            });
        }
    };

    const linkClassName = cx(
        'tw-relative tw-mb-0 tw-ml-1.5 tw-flex tw-w-full tw-min-w-0 tw-cursor-pointer tw-items-center tw-gap-2 tw-rounded-lg tw-px-1.5 tw-py-1 tw-text-left tw-text-sm tw-font-medium tw-text-accents-8 tw-transition-colors group-hover:tw-bg-accents-0 group-hover:tw-text-success',
        isActive &&
            'tw-font-semibold before:tw-absolute before:-tw-left-1.5 before:tw-bottom-0.5 before:tw-top-0.5 before:tw-inline-block before:tw-w-[3px] before:tw-rounded before:tw-bg-primary before:tw-content-[""]'
    );

    const linkContent = (
        <>
            {tab.icon && <FontAwesomeIcon icon={tab.icon} className="tw-min-w-4" />}
            <span className="tw-truncate">{tab.title}</span>
            {/* Push the Beta badge to the right edge, away from the title. */}
            {tab.beta && (
                <span className="tw-ml-auto tw-inline-flex">
                    <BetaBadge />
                </span>
            )}
            {typeof tab.badge === 'number' && tab.badge > 0 && (
                <Badge className="tw-ml-auto" flat color="error" size="xs">
                    {tab.badge < 100 ? tab.badge : '99+'}
                </Badge>
            )}
            {tab.tooltip && (
                <span className="tw-ml-auto tw-shrink-0">
                    <InfoTooltip tooltip={tab.tooltip} />
                </span>
            )}
            {/* Trailing external-link indicator for tabs that open in a new tab. */}
            {isExternal && (
                <FontAwesomeIcon
                    icon={faArrowUpRightFromSquare}
                    className="tw-ml-auto tw-shrink-0 tw-text-xs tw-text-accents-6"
                />
            )}
        </>
    );

    return (
        <li key={tab.key} className="pl-1 tw-group tw-flex tw-min-w-0 tw-items-center">
            {/* External tabs can't be favorited — they aren't module routes; keep a spacer for alignment. */}
            {isExternal ? (
                <span className="mr-1 tw-h-5 tw-w-5 tw-shrink-0" aria-hidden />
            ) : (
                <button
                    type="button"
                    onClick={handleFavoriteToggle}
                    className={cx(
                        'mr-1 tw-flex tw-h-5 tw-w-5 tw-items-center tw-justify-center tw-rounded-full tw-border-none tw-bg-transparent tw-opacity-0 tw-transition-colors tw-duration-150 group-hover:tw-opacity-100',
                        isFavorite ? 'tw-text-warning tw-opacity-100' : 'tw-text-accents-5 hover:tw-text-warning'
                    )}
                    aria-pressed={isFavorite}
                    aria-label={isFavorite ? 'Aus Favoriten entfernen' : 'Zu Favoriten hinzufügen'}
                >
                    <FontAwesomeIcon icon={isFavorite ? faSolidStar : faRegularStar} className="tw-h-3 tw-w-3" />
                </button>
            )}
            {isExternal ? (
                <a href={href} target="_blank" rel="noopener noreferrer" className={linkClassName}>
                    {linkContent}
                </a>
            ) : (
                <Link href={href} className={linkClassName}>
                    {linkContent}
                </Link>
            )}
        </li>
    );
}

function QuickActionButton({ action, priority = false }: { action: QuickAction; priority?: boolean }) {
    const icon = action.icon ?? faPlus;
    const commonClassName = cx(
        'tw-inline-flex tw-h-6 tw-shrink-0 tw-items-center tw-justify-center tw-gap-1.5 tw-rounded-md tw-border tw-border-solid tw-transition-colors',
        'tw-w-6 tw-border-transparent tw-bg-transparent tw-text-accents-6 hover:tw-border-accents-2 hover:tw-bg-accents-1 hover:tw-text-accents-8'
    );
    const content = (
        <>
            <FontAwesomeIcon icon={icon} className={priority ? 'tw-text-[10px]' : 'tw-text-[11px]'} />
        </>
    );

    return (
        <Tooltip tooltip={action.label} placement="right">
            <button type="button" onClick={action.onClick} aria-label={action.label} className={commonClassName}>
                {content}
            </button>
        </Tooltip>
    );
}

SideBarAccordion.displayName = 'SideBarAccordion';

export default SideBarAccordion;
